// Las inscripciones de HealthSherpa de UN cliente, dentro de su ficha.
//
// POR QUE EXISTE. Lissette, viendo el cartel verde en la pantalla de
// Inscribir (2026-11-03): "me gustaría que esta información saliera en la
// ficha del cliente". Tenía razón y el motivo es de trabajo: cuando abres a
// un cliente para llamarlo, lo que quieres saber es si su inscripción salió
// y con qué número — no acordarte de ir a otra pantalla a buscarlo.
//
// DONDE VA: en la pestaña POLIZAS de la ficha, encima de las pólizas. Ahí es
// donde se piensa en la cobertura de esa persona, y es el sitio donde ya
// miras cuando te preguntan "¿esto quedó?".
//
// AQUI NO VAN LOS ENLACES de inscripción (Lissette, 2026-11-03: "no tienes
// que colocar enlace para cliente ni enlace de compra"). Un enlace sirve
// para MANDARLO, y eso se hace en la pantalla de Inscribir, en el momento.
// En la ficha, semanas después, lo que se consulta es si la inscripción
// quedó y con qué número; los enlaces solo serían dos filas de ruido encima
// de las pólizas. Siguen estando en Inscribir, que es donde se usan.
//
// NO SE AÑADIO UNA PESTAÑA NUEVA a propósito. La ficha ya tiene veintiuna, y
// una más por algo que la mayoría de los clientes nunca va a tener es una
// pestaña vacía en cada ficha para siempre.
//
// SI NO HAY INSCRIPCIONES NO SE PINTA NADA, y eso no choca con la regla de
// la casa de "nunca escondas la interfaz porque un campo esté vacío". Esa
// regla protege los CAMPOS de una ficha: si el teléfono desaparece cuando
// está vacío, parece que se perdió. Aquí no hay campo que perder — es una
// sección que solo existe cuando hubo una inscripción por Deskte, igual que
// no sale una sección de "firmas" en un cliente que nunca firmó nada.
//
// SE CALLA EN TODOS LOS FALLOS, a posta: un asistente no tiene permiso
// (403), la demo pública no tiene servidor, y una agente sin HealthSherpa
// no tiene nada que enseñar. En los tres casos lo correcto es no pintar
// nada, no un cartel de error en la ficha de un cliente que no tiene que
// ver con eso.
import React, { useState, useEffect } from "react";
import { Copy, Check, HeartPulse } from "lucide-react";
import { getHealthSherpaEnrollmentSessions } from "../api.js";

const dia = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v).slice(0, 10);
  // Partes LOCALES, nunca toISOString: en UTC el día se corre de madrugada.
  return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
};

export function ClientHealthSherpa({ clientId, lang = "es" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [sesiones, setSesiones] = useState([]);
  const [copiado, setCopiado] = useState("");

  useEffect(() => {
    if (!clientId) return undefined;
    let vivo = true;
    getHealthSherpaEnrollmentSessions(clientId)
      .then((r) => { if (vivo) setSesiones(r?.sesiones || []); })
      .catch(() => { if (vivo) setSesiones([]); });
    return () => { vivo = false; };
  }, [clientId]);

  if (!sesiones.length) return null;

  const copiar = (texto, cual) => {
    try {
      navigator.clipboard.writeText(texto);
      setCopiado(cual);
      setTimeout(() => setCopiado(""), 2000);
    } catch { /* el valor se ve igual al lado */ }
  };

  const boton = {
    display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px",
    background: "transparent", color: "#00d4ff", border: "none", borderRadius: 6,
    fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  };

  return (
    <div style={{
      marginBottom: "1rem", padding: ".85rem 1rem", borderRadius: 10,
      background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: ".6rem" }}>
        <HeartPulse size={15} color="#00d4ff" />
        <span style={{ fontWeight: 700, fontSize: ".85rem" }}>
          {t("Inscripciones de HealthSherpa", "HealthSherpa enrollments")}
        </span>
      </div>

      <div style={{ display: "grid", gap: ".6rem" }}>
        {sesiones.map((s) => (
          <div key={s.id} style={{ fontSize: ".8rem", lineHeight: 1.6 }}>
            <div style={{ color: "var(--c-text2,#7aa3c0)" }}>
              {s.planYear}
              {" · "}
              {s.flow === "self_service"
                ? t("el cliente solo", "client alone")
                : t("acompañado", "agent-assisted")}
              {s.createdAt && <>{" · "}{dia(s.createdAt)}</>}
            </div>

            {/* El número de confirmación es el de HEALTHSHERPA, el que la
                agente busca en el portal de ellos. Solo existe cuando la
                solicitud ya apareció en su estado. */}
            {s.hsConfirmationId ? (
              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span style={{ color: "#4ade80", fontWeight: 600 }}>
                  {t("Ya inscrita", "Already enrolled")}
                </span>
                <span style={{ color: "var(--c-text2,#7aa3c0)" }}>
                  {t("N.º de confirmación:", "Confirmation no.:")}
                </span>
                <code style={{ fontFamily: "var(--font-mono,monospace)" }}>{s.hsConfirmationId}</code>
                <button onClick={() => copiar(s.hsConfirmationId, s.hsConfirmationId)} style={boton}>
                  {copiado === s.hsConfirmationId ? <Check size={11} /> : <Copy size={11} />}
                  {copiado === s.hsConfirmationId ? t("Copiado", "Copied") : t("Copiar", "Copy")}
                </button>
              </div>
            ) : (
              <div style={{ color: "var(--c-text3,#45688a)" }}>
                {t(
                  "Esperando a que HealthSherpa procese la solicitud. Puede tardar hasta el día siguiente.",
                  "Waiting for HealthSherpa to process the application. It can take until the next day.",
                )}
              </div>
            )}

          </div>
        ))}
      </div>
    </div>
  );
}

export default ClientHealthSherpa;
